import React, { useState, useCallback } from 'react';
import * as microsoftTeams from '@microsoft/teams-js';

/**
 * SSO Authentication Service Hook
 * Provides SSO authentication functionality using Teams SDK and OBO flow
 * 
 * @returns {object} Authentication state and methods
 */
export const useSSOAuth = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Get client-side token from Teams
     */
    const getClientSideToken = useCallback(async () => {
        try {
            const token = await microsoftTeams.authentication.getAuthToken();
            return token;
        } catch (error) {
            console.error('Failed to get client-side token:', error);
            throw new Error('Teams-Authentifizierungstoken konnte nicht abgerufen werden');
        }
    }, []);

    /**
     * Exchange client-side token for server-side token via backend
     */
    const getServerSideToken = useCallback(async (clientSideToken, scopes = []) => {
        try {
            const context = await microsoftTeams.app.getContext();
            const authEndpoint = process.env.REACT_APP_AUTH_ENDPOINT || 'http://localhost:3978/api/auth/token';

            const response = await fetch(authEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    tid: context.user.tenant.id,
                    token: clientSideToken,
                    scopes: scopes // Pass requested scopes to backend
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                const errorMsg = errorData.error || errorData.message || 'Token-Austausch fehlgeschlagen';
                throw new Error(errorMsg);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to exchange token:', error);
            throw error;
        }
    }, []);

    /**
     * Request user consent for additional permissions
     */
    const requestConsent = useCallback(async () => {
        try {
            let startLoginPageUrl = process.env.REACT_APP_START_LOGIN_PAGE_URL || '/auth-start';
            if (startLoginPageUrl.startsWith('/')) {
                startLoginPageUrl = window.location.origin + startLoginPageUrl;
            }

            const result = await microsoftTeams.authentication.authenticate({
                url: startLoginPageUrl,
                width: 600,
                height: 535
            });

            return result;
        } catch (error) {
            console.error('Consent failed:', error);
            throw new Error('Benutzer-Zustimmung wurde nicht erteilt');
        }
    }, []);

    /**
     * Main authentication flow
     */
    const login = useCallback(async (scopes = []) => {
        setLoading(true);
        setError(null);

        try {
            // Step 1: Get client-side token from Teams
            const clientToken = await getClientSideToken();

            // Step 2: Exchange for server-side token
            const userData = await getServerSideToken(clientToken, scopes);

            // Step 3: Set user data
            setUser(userData);
            return userData;

        } catch (err) {
            if (err.message && (err.message.includes('invalid_grant') || err.message.includes('consent_required'))) {
                console.log('Consent required detected in SSO service');
                setError('Zustimmung erforderlich. Bitte erteilen Sie die Berechtigungen.');
                return { needsConsent: true };
            } else {
                console.error('SSO Login failed with non-consent error:', err);
                setError(err.message || 'Authentifizierung fehlgeschlagen');
                throw err;
            }
        } finally {
            setLoading(false);
        }
    }, [getClientSideToken, getServerSideToken]);

    /**
     * Handle consent and retry login
     */
    const loginWithConsent = useCallback(async (scopes = []) => {
        try {
            setLoading(true);
            setError(null);

            // Request consent
            await requestConsent();

            // Retry login after consent
            return await login(scopes);
        } catch (err) {
            setError(err.message || 'Authentifizierung mit Zustimmung fehlgeschlagen');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [requestConsent, login]);

    /**
     * Utility to get a token (internal use or for the hook)
     */
    const getToken = useCallback(async (scopes = []) => {
        try {
            const clientToken = await getClientSideToken();
            const data = await getServerSideToken(clientToken, scopes);
            return data.accessToken;
        } catch (err) {
            console.error('Failed to get token:', err);
            throw err;
        }
    }, [getClientSideToken, getServerSideToken]);

    /**
     * Logout (clear user state)
     */
    const logout = useCallback(() => {
        setUser(null);
        setError(null);
    }, []);

    return {
        user,
        loading,
        error,
        login,
        loginWithConsent,
        getToken,
        logout
    };
};

/**
 * SSO Authentication Component
 * Example usage component that demonstrates SSO authentication
 */
const SSOAuthService = ({ onAuthSuccess, onAuthError }) => {
    const { user, loading, error, login, loginWithConsent } = useSSOAuth();
    const [needsConsent, setNeedsConsent] = useState(false);

    React.useEffect(() => {
        // Auto-login when component mounts
        const initAuth = async () => {
            const result = await login();
            if (result && result.needsConsent) {
                setNeedsConsent(true);
            } else if (result && onAuthSuccess) {
                onAuthSuccess(result);
            }
        };

        initAuth().catch((err) => {
            if (onAuthError) {
                onAuthError(err);
            }
        });
    }, []);

    const handleConsent = async () => {
        try {
            const result = await loginWithConsent();
            if (result && onAuthSuccess) {
                onAuthSuccess(result);
            }
            setNeedsConsent(false);
        } catch (err) {
            if (onAuthError) {
                onAuthError(err);
            }
        }
    };

    if (loading) {
        return <div>Authentifizierung...</div>;
    }

    if (needsConsent) {
        return (
            <div>
                <p>Zusätzliche Berechtigungen erforderlich.</p>
                <button onClick={handleConsent}>Berechtigungen erteilen</button>
            </div>
        );
    }

    if (error) {
        return <div>Fehler: {error}</div>;
    }

    if (user) {
        return <div>Angemeldet als: {user.displayName || user.userPrincipalName}</div>;
    }

    return null;
};

export default SSOAuthService;
